import { useCallback, useMemo, useState } from 'react';

import { ScanDataWeekEndingOverviewFragment } from '@occc/graphql';
import {
  DateTimeFormat,
  DateTimeFormatTZ,
  formatDateTime,
  formatDateTimeTZ,
  getUserTimezone,
} from '@occc-app/lib/date';
import { LoadedData } from '@occc/utils';
import { Feature, useIsUserPermittedTo } from '@occc/utils-access-control';

import { PastSubmissionsTable } from './components/past-submissions-table';
import { ScanDataWeeklyFileOverviewContainer } from './components/scan-data-weekly-file-overview.container';
import { useScanDataWeeklyFileSubmit } from './hooks/use-scan-data-weekly-file-submit.hook';
import { ScanDataMissingStoresModal } from './components/scan-data-missing-stores-modal.component';

export type ScanDataWeeklyFileProps = {
  weekEndingDate: Date;
  weekEndingOverviewLoadedData: LoadedData<ScanDataWeekEndingOverviewFragment>;
};

export const ScanDataWeeklyFileContainer = ({
  weekEndingDate,
  weekEndingOverviewLoadedData,
}: ScanDataWeeklyFileProps) => {
  const isSubmitAllowed = useIsUserPermittedTo(Feature.SCAN_DATA_SUBMIT_WEEKLY_FILE);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { loading, error, data } = weekEndingOverviewLoadedData;

  const weekEnding = formatDateTime(weekEndingDate, DateTimeFormat.DateSlashed);
  const timezone = useMemo(() => {
    return formatDateTimeTZ(new Date(), getUserTimezone(), DateTimeFormatTZ.ShortTimeZone);
  }, []);

  const lastSftpPullDateTime = useMemo(() => {
    if (!data?.lastTransactionLogPulledAt) {
      return null;
    }

    return `${formatDateTime(
      new Date(data.lastTransactionLogPulledAt),
      DateTimeFormat.DateTimeSlashed12h,
    )} ${timezone}`;
  }, [data?.lastTransactionLogPulledAt, timezone]);

  const onMissingStoresModalClose = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const {
    fileDisplayStatus,
    canSubmit,
    isConfirmDialogOpen,
    setIsConfirmDialogOpen,
    handleSubmitClick,
    handleConfirmSubmit,
    isSubmitting,
  } = useScanDataWeeklyFileSubmit({
    data,
    submissionDate: weekEndingOverviewLoadedData.data?.lastTransactionLogPulledAt,
  });

  return (
    <>
      <ScanDataWeeklyFileOverviewContainer
        loading={loading}
        error={error}
        data={data}
        weekEnding={weekEnding}
        lastSftpPullDateTime={lastSftpPullDateTime}
        canSubmit={canSubmit}
        isSubmitting={isSubmitting}
        onSubmitClick={handleSubmitClick}
        fileDisplayStatus={fileDisplayStatus}
        onViewStoresClick={() => setIsModalOpen(true)}
        isConfirmDialogOpen={isConfirmDialogOpen}
        setIsConfirmDialogOpen={setIsConfirmDialogOpen}
        handleConfirmSubmit={handleConfirmSubmit}
        isSubmitAllowed={isSubmitAllowed}
      />

      <PastSubmissionsTable />

      {isModalOpen && <ScanDataMissingStoresModal open onClose={onMissingStoresModalClose} />}
    </>
  );
};
